import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useTermoListaFilters } from "../hooks/termo_lista/filters";
import { useTermoListaPagination } from "../hooks/termo_lista/pagination";
import { TermoCadastroForm } from "../schemas/termo_lista/termoListaCadastro";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { Termo } from "../../domain/models/termo";

type LoadTermoListaState = {
    status?: number,
    codigo?: string,
    descricao?: string,
    nome?: string,

    currentPage?: number;
    rowsPerPage?: number;

}

export const useTermoListaViewModel = () => {

    const {
        entregaObras: {
            getAllTermoUseCase,
            getTermoByIdUseCase,
            deleteTermoUseCase,
            createTermoUseCase,
            updateTermoUseCase,

        },
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
        }
    } = useDependencyInjection();


    const navigate = useNavigate();
    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openCadastroModal, setOpenCadastroModal] = useState(false);

    const [selectedIdTermo, setSelectedIdTermo] = useState<number>();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [termoLista, setTermoLista] = useState<Termo[]>();
    const [selectedTermo, setSelectedTermo] = useState<Termo | undefined>();


    const filters = useTermoListaFilters();
    const pagination = useTermoListaPagination();


    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })


    //#region CARREGAMENTO.

    const loadTermoLista = useCallback(async (funcState?: LoadTermoListaState) => {
        try {
            setLoading(true);
            const filteredStatus = evaluateParamValue(funcState?.status, filters.status);
            const filteredCodigo = evaluateParamValue(funcState?.codigo, filters.codigo);
            const filteredNome = evaluateParamValue(funcState?.nome, filters.nome);
            const filteredDescricao = evaluateParamValue(funcState?.descricao, filters.descricao);

            const filteredPage = funcState?.currentPage ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllTermoUseCase.execute(
                filteredCodigo ?? '',
                filteredDescricao ?? '',
                filteredNome ?? '',
                filteredStatus ?? 2,

                filteredPage,
                filteredRowsPerPage,
            );

            setTermoLista(getAllTermoUseCase.items);

            pagination.setRowsPerPage(getAllTermoUseCase.pageSize);
            pagination.setTotalItems(getAllTermoUseCase.totalItems);
        } catch (e: unknown) {
            setTermoLista([]);
            pushErrorNotification("Error", processError(e, "Falha ao buscar lista de termos!"));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        pagination,
        getAllTermoUseCase,
        pushErrorNotification
    ]);


    const loadTermoById = useCallback(async (idTermo: number) => {
        try {
            setLoading(true);
            const termo = await getTermoByIdUseCase.execute(idTermo);
            setSelectedTermo(termo);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar termo!'));
        } finally {
            setLoading(false);
        }
    }, [
        getTermoByIdUseCase,
        pushErrorNotification]);




    //#endregion






    //#region PAGE START

    usePageStartBootstrap(async () => {
        setPageLoading(true);
        try {
            if (termoLista === undefined) {
                await loadTermoLista();
            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de termos!")[0]);
        } finally {
            setPageLoading(false);
        }
    });


    //#endregion




    //#region NOVO/EDITAR.

    const handleTermoCadastroOpenState = useCallback(async (idTermo: number | null, openState: boolean) => {
        setOpenCadastroModal(openState);
        try {
            if (openState) {
                if (idTermo) {
                    await loadTermoById(idTermo);
                } else {
                    setSelectedTermo(undefined);
                }
            } else {
                setSelectedTermo(undefined);
            }

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao iniciar criação de novo termo!'));
        }
    }, [
        loadTermoById,
        setSelectedTermo,
        setOpenCadastroModal,
        pushErrorNotification
    ]);



    const handleSubmitTermo = useCallback(async (form: TermoCadastroForm) => {
        try {
            setSaving(true);

            if (form.idTermo) {
                await updateTermoUseCase.execute(
                    form.idTermo,
                    form.codigo,
                    form.descricao,
                    form.nome ?? '',
                    form.ativo,
                );
                pushInfoNotification("Sucesso", " Termo atualizado com sucesso!", { isAutoFinish: true });
            } else {
                await createTermoUseCase.execute(
                    form.codigo,
                    form.descricao,
                    form.nome ?? '',

                )

                pushInfoNotification("Sucesso", " Termo criado com sucesso!", { isAutoFinish: true });
            }

            setOpenCadastroModal(false);
            setSelectedTermo(undefined);
            await loadTermoLista();
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar termo!"));
        } finally {
            setSaving(false);
        }
    }, [
        createTermoUseCase,
        updateTermoUseCase,
        selectedTermo,

        loadTermoLista,
        pushErrorNotification,
        pushInfoNotification]);

    //#endregion





    //#region PAGINAÇÃO.

    const handleNewTermo = useCallback(async () => {
        try {
            setLoading(true);
            setOpenCadastroModal(true);
            setSelectedTermo(undefined);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de termos!'));
        } finally {
            setLoading(false);

        }
    }, [loadTermoLista]);


    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadTermoLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de termos!'));
        } finally {
            setLoading(false);
        }
    }, [loadTermoLista]);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadTermoLista({ currentPage: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de termos!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadTermoLista]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadTermoLista({ rowsPerPage, currentPage: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de termos!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadTermoLista]);

    //#endregion





    //#region DELECAO 

    const handleDeleteOpenState = useCallback((idTermo: number | null, openState: boolean) => {
        if (openState) {
            if (idTermo) {
                setSelectedIdTermo(idTermo);
                setOpenDeleteModal(true);
            } else {
                setSelectedIdTermo(undefined);
                setOpenDeleteModal(false);
            }
        }
        setOpenDeleteModal(openState);
    }, []);


    const handleConfirmarDeletarTermo = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdTermo) {
                await deleteTermoUseCase.execute(selectedIdTermo);
                pushInfoNotification("Sucesso", " Termo deletado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel deletar Termo!", { isAutoFinish: true });
            }
            await loadTermoLista()
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Termo!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdTermo(undefined)
        }
    }, [
        selectedIdTermo,
        deleteTermoUseCase,
        loadTermoLista,
        pushErrorNotification,
        pushInfoNotification]);

    //#endregion





    //#region PESQUISA.

    /**
     * 
     */
    const handleCodigoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setCodigo(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadTermoLista({ codigo: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar termos!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadTermoLista, pushErrorNotification]
    );

    const handleNomeChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setNome(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadTermoLista({ nome: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar termos!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadTermoLista, pushErrorNotification]
    );

    const handleDescricaoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setDescricao(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadTermoLista({ descricao: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar termos!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadTermoLista, pushErrorNotification]
    );

    const handleStatusChange = useDebouncedCallback(
        debouncedRef,
        (newValue: number | null) => {

            filters.setStatus(newValue ?? 2);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadTermoLista({ status: newValue ?? 2, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar termos!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadTermoLista, pushErrorNotification]);


    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadTermoLista({
                codigo: '',
                descricao: '',
                nome: '',
                status: 2,
                currentPage: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar termos!'));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        pagination,
        loadTermoLista,
        pushErrorNotification]);


    //#endregion 


    //#region History

    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(selectedTermo?.idTermo ?? 0, LogTipoTabela.TERMO, true);
            }
        }
    }, [
        selectedTermo,
        changeHistory,
    ]);

    //#endregion

    //#region ITENS CHECKLIST ENTREGA



    //#endregion

    return {
        loading,
        saving,
        openDeleteModal,
        openCadastroModal,
        termoLista: termoLista ?? [],
        filters,
        pagination,
        selectedTermo,
        errorMessage,
        changeHistory,

        handleNewTermo,
        handleNomeChange,
        handleSubmitTermo,
        handleTermoCadastroOpenState,
        handleConfirmarDeletarTermo,
        handleDeleteOpenState,
        handleOpenChangeHistory,
        handleCodigoChange,
        handleDescricaoChange,
        handleStatusChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleClearFilters,

    };
}


